import { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'motion/react';
import CountUp from '@/components/landing/common/CountUp';
import { breakpoints } from '@/layouts/breakpoints';

const StyledComp = styled.div`
  padding: 48px 56px 64px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${breakpoints.medium}) {
    padding: 48px 20px 64px;
  }

  .tag {
    font-size: 13px;
    color: #888;
    letter-spacing: 0.02em;
    margin-bottom: 36px;
  }

  .header {
    display: flex;
    align-items: flex-start;
    gap: 80px;
    margin-bottom: 64px;

    @media (max-width: ${breakpoints.medium}) {
      gap: 0px;
      flex-direction: column;
    }
  }

  .headline {
    font-size: clamp(36px, 4vw, 52px);
    font-weight: 500;
    color: #111;
    line-height: 1.1;
    letter-spacing: -0.02em;
    min-width: 60%;
    max-width: 520px;
    flex-shrink: 0;
    margin: 0;
  }

  .subheadline {
    font-size: 15px;
    color: #666;
    line-height: 1.6;
    max-width: 380px;
    padding-top: 6px;
    margin: 0;
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border: 1px solid #d8d8d2;
    border-radius: 4px;
    overflow: hidden;
    @media (max-width: ${breakpoints.medium}) {
      grid-template-columns: 100%;
    }
  }

  .left-cell {
    border-right: 1px solid #d8d8d2;
    padding: 40px 40px 36px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 560px;
    background: #fff;

    @media (max-width: ${breakpoints.medium}) {
      border-right: none;
    }
  }

  .right-col {
    display: flex;
    flex-direction: column;
  }

  .right-cell {
    padding: 40px 40px 36px;
    background: #fff;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &:first-child {
      border-bottom: 1px solid #d8d8d2;
    }
  }

  .big-stat {
    font-size: 64px;
    font-weight: 400;
    color: #111;
    letter-spacing: -0.03em;
    line-height: 1;
    margin: 0 0 auto;
    @media (max-width: ${breakpoints.small}) {
      font-size: 48px;
    }
  }

  .chart-area {
    flex: 1;
    display: flex;
    align-items: flex-end;
    padding: 24px 0 32px;
  }

  .cell-footer {
    margin-top: 8px;
  }

  .cell-title {
    font-size: 15px;
    font-weight: 600;
    color: #111;
    margin: 0 0 8px;
    letter-spacing: -0.01em;
  }

  .cell-desc {
    font-size: 14px;
    color: #666;
    line-height: 1.55;
    margin: 0;
  }

  .cell-desc strong {
    font-weight: 600;
    color: #444;
  }
`;

const AnimatedStat = ({ value, delay = 0 }: { value: string; delay?: number }) => {
  const match = value.match(/^([\d.]+)(.*)$/);
  if (!match) return <>{value}</>;
  return (
    <>
      <CountUp to={parseFloat(match[1])} delay={delay} duration={2} />
      {match[2]}
    </>
  );
};

const ChartSVG = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '0px' });
  const points = [
    [0, 220], [30, 215], [70, 210], [110, 205], [140, 200],
    [170, 198], [200, 195], [230, 192], [250, 188], [270, 183],
    [290, 175], [305, 165], [318, 148], [330, 125], [338, 95],
    [345, 60], [350, 20],
  ];

  // Converts an array of points into a smooth SVG path using
  // Catmull-Rom -> cubic Bezier conversion (passes through every point)
  const smoothPath = (pts) => {
    if (pts.length < 3) {
      return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');
    }
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i === 0 ? 0 : i - 1];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2 < pts.length ? i + 2 : i + 1];

      const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
      const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
      const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
      const cp2y = p2[1] - (p3[1] - p1[1]) / 6;

      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`;
    }
    return d;
  };

  const pathD = smoothPath(points);
  const areaD =
    pathD +
    ` L ${points[points.length - 1][0]} 260 L ${points[0][0]} 260 Z`;

  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      <svg
        viewBox="0 0 360 270"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: '100%', height: '100%' }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#999" />
            <stop offset="75%" stopColor="#bbb" />
            <stop offset="100%" stopColor="#e85d26" />
          </linearGradient>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e85d26" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#e85d26" stopOpacity="0.01" />
          </linearGradient>
          <pattern id="dots" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="#ccc" opacity="0.6" />
          </pattern>
        </defs>
        <motion.path
          d={areaD}
          fill="url(#dots)"
          initial={{ opacity: 0 }}
          animate={{ opacity: isInView ? 0.7 : 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        />
        <motion.path
          d={areaD}
          fill="url(#areaGrad)"
          initial={{ opacity: 0 }}
          animate={{ opacity: isInView ? 1 : 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        />
        <motion.path
          d={pathD}
          fill="none"
          stroke="url(#lineGrad)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: isInView ? 1 : 0 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
        <motion.circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r="5"
          fill="#e85d26"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: isInView ? 1 : 0, scale: isInView ? 1 : 0 }}
          transition={{ duration: 0.4, delay: 1.3, ease: 'easeOut' }}
        />
        <motion.circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r="9"
          fill="#e85d26"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: isInView ? 0.2 : 0, scale: isInView ? 1 : 0 }}
          transition={{ duration: 0.4, delay: 1.3, ease: 'easeOut' }}
        />
      </svg>
    </div>
  );
};

/**
 * @typedef {Object} StatCell
 * @property {string} stat        - The large number/label e.g. "87%"
 * @property {string} title       - Bold footer title e.g. "87% Automated across live audits."
 * @property {string} desc        - Footer description text
 * @property {boolean} [boldDesc] - Whether to wrap desc in <strong>
 * @property {boolean} [showChart] - Whether to render the chart (left cell only)
 */

/**
 * @param {{
 *   headline?: string,
 *   subheadline?: string,
 *   leftCell?: StatCell,
 *   rightCells?: [StatCell, StatCell]
 * }} props
 */
const ResearchStats = ({
  headline = '',
  subheadline = '',
  leftCell = {
    stat: '16',
    title: '16 Essential Datasets, One API.',
    desc: 'Market data, fundamentals, SEC filings, and alternative data delivered through a single financial data API.',
    showChart: true,
  },
  rightCells = [
    {
      stat: '24/7',
      title: 'Reliable, Always-On Access.',
      desc: 'Mission-critical data infrastructure engineered for continuous availability and consistent response times.',
    },
    {
      stat: '100%',
      title: '100% Structured Data.',
      desc: 'Every endpoint returns clean, normalized, machine-readable data ready for research, models, and applications.',
      boldDesc: true,
    },
  ],
}) => {
  return (
    <StyledComp>
      <div className="header">
        <h2 className="headline serif">{headline}</h2>
        <p className="subheadline">{subheadline}</p>
      </div>

      <div className="grid">
        <div className="left-cell">
          <div className="big-stat sans"><AnimatedStat value={leftCell.stat} /></div>
          {leftCell.showChart && (
            <div className="chart-area">
              <ChartSVG />
            </div>
          )}
          <div className="cell-footer">
            <p className="cell-title">{leftCell.title}</p>
            <p className="cell-desc">
              {leftCell.boldDesc ? <strong>{leftCell.desc}</strong> : leftCell.desc}
            </p>
          </div>
        </div>

        <div className="right-col">
          {rightCells.map((cell, i) => (
            <div className="right-cell" key={i}>
              <div className="big-stat sans"><AnimatedStat value={cell.stat} delay={i * 0.15} /></div>
              <div className="cell-footer">
                <p className="cell-title">{cell.title}</p>
                <p className="cell-desc">
                  {cell.boldDesc ? <strong>{cell.desc}</strong> : cell.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </StyledComp>
  );
};

export default ResearchStats;
